import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Divider, Chip, Stack,
  Accordion, AccordionSummary, AccordionDetails, Alert, CircularProgress
} from '@mui/material';
import {
  ExpandMore, Help, Router, SolarPower, PhoneAndroid,
  VpnKey, Groups, Speed, Settings
} from '@mui/icons-material';

/**
 * HelpView – Renders help content from /content/help.md
 *
 * Source file: someac/client/docs/help.md
 * The markdown is copied to public/content/ at build time.
 *
 * Markdown conventions:
 *   ## Section Title        → accordion panel (icon mapped by title)
 *   ### Subtitle            → subtitle heading
 *   ---                     → horizontal divider
 *   **bold**                → <strong>
 *   - list item             → bullet list
 *   - **Label:** value      → chip + value row
 *   ```code```              → info card (auto-detects key: value tables)
 *   > [!WARNING] text       → warning alert
 *   Blank-line-separated    → paragraph breaks
 */

/* ── Icon map ────────────────────────────────────────────────────── */
const SECTION_ICONS = {
  'System Overview':        <SolarPower sx={{ color: '#f59e0b' }} />,
  'Sabal App Setup':        <PhoneAndroid sx={{ color: '#3DDC84' }} />,
  'Sabal Controller':       <Router sx={{ color: '#6366f1' }} />,
  'Service Panel Code':     <VpnKey sx={{ color: '#dc2626' }} />,
  'Telemetry & Monitoring': <Speed sx={{ color: '#0891b2' }} />,
  'Administration':         <Settings sx={{ color: '#7c3aed' }} />,
  'Network & Connectivity': <Groups sx={{ color: '#059669' }} />,
};
const DEFAULT_ICON = <Help sx={{ color: '#6b7280' }} />;

/* ── Inline renderer (**bold**) ──────────────────────────────────── */
function renderInline(text) {
  if (!text) return text;
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
}

/* ── Block parser ────────────────────────────────────────────────── */
function parseBlocks(content) {
  const blocks = [];
  const lines = content.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Skip blank lines
    if (line.trim() === '') { i++; continue; }

    // Horizontal rule
    if (/^---\s*$/.test(line.trim())) {
      blocks.push({ type: 'divider' });
      i++; continue;
    }

    // H3 heading
    if (line.startsWith('### ')) {
      blocks.push({ type: 'heading3', text: line.slice(4).trim() });
      i++; continue;
    }

    // Fenced code block
    if (line.trim().startsWith('```')) {
      i++;
      const codeLines = [];
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // skip closing ```
      blocks.push({ type: 'code', lines: codeLines });
      continue;
    }

    // Alert block-quote  > [!WARNING]
    if (line.trim().startsWith('> [!WARNING]')) {
      i++;
      const alertLines = [];
      while (i < lines.length && lines[i].trim().startsWith('>')) {
        alertLines.push(lines[i].replace(/^>\s?/, '').trim());
        i++;
      }
      blocks.push({ type: 'alert', text: alertLines.join(' ') });
      continue;
    }

    // List items (- ...)
    if (line.trim().startsWith('- ')) {
      const items = [];
      while (i < lines.length && lines[i].trim().startsWith('- ')) {
        items.push(lines[i].trim().slice(2));
        i++;
      }
      // Detect chip-list pattern: every item is **Label:** Value
      const isChipList = items.every(item => /^\*\*[^*]+:\*\*\s/.test(item));
      if (isChipList) {
        blocks.push({
          type: 'chipList',
          items: items.map(item => {
            const match = item.match(/^\*\*([^*]+):\*\*\s+(.*)/);
            return { label: match[1], value: match[2] };
          }),
        });
      } else {
        blocks.push({ type: 'list', items });
      }
      continue;
    }

    // Paragraph — collect lines until a blank line or block marker
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].startsWith('### ') &&
      !lines[i].trim().startsWith('```') &&
      !lines[i].trim().startsWith('> [!') &&
      !lines[i].trim().startsWith('- ') &&
      !/^---\s*$/.test(lines[i].trim())
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length) {
      blocks.push({ type: 'paragraph', text: paraLines.join(' ') });
    }
  }

  // Mark paragraphs / lists after a heading3 as indented
  for (let b = 1; b < blocks.length; b++) {
    if (blocks[b - 1].type === 'heading3' &&
        (blocks[b].type === 'paragraph' || blocks[b].type === 'chipList')) {
      blocks[b].indented = true;
    }
  }

  return blocks;
}

/* ── Document parser ─────────────────────────────────────────────── */
function parseHelpMarkdown(md) {
  // Front matter
  const fmMatch = md.match(/^---\n([\s\S]*?)\n---/);
  const frontMatter = {};
  let body = md;

  if (fmMatch) {
    fmMatch[1].split('\n').forEach(line => {
      const idx = line.indexOf(':');
      if (idx > 0) {
        frontMatter[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      }
    });
    body = md.slice(fmMatch[0].length).trim();
  }

  // Split on ## headers
  const parts = body.split(/^## /m).filter(Boolean);
  const sections = parts.map(part => {
    const nl = part.indexOf('\n');
    const title = (nl > 0 ? part.slice(0, nl) : part).trim();
    const content = nl > 0 ? part.slice(nl + 1).trim() : '';
    return { title, blocks: parseBlocks(content) };
  });

  return { frontMatter, sections };
}

/* ── Block renderer ──────────────────────────────────────────────── */
function RenderBlock({ block, idx }) {
  switch (block.type) {
    case 'divider':
      return <Divider sx={{ my: 2 }} />;

    case 'heading3':
      return (
        <Typography variant="subtitle2" gutterBottom
          sx={{ fontWeight: 600, mt: idx > 0 ? 1 : 0 }}>
          {block.text}
        </Typography>
      );

    case 'alert':
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">{renderInline(block.text)}</Typography>
        </Alert>
      );

    case 'code': {
      const nonEmpty = block.lines.filter(l => l.trim());
      const isKv = nonEmpty.length > 0 &&
        nonEmpty.every(l => /^[A-Za-z][^:]*:\s+/.test(l));

      if (isKv) {
        return (
          <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f8f9fa' }}>
            <CardContent>
              <Stack spacing={1}>
                {nonEmpty.map((line, j) => {
                  const [key, ...rest] = line.split(/:\s+/);
                  return (
                    <Stack key={j} direction="row" spacing={2}>
                      <Typography variant="body2"
                        sx={{ fontWeight: 600, minWidth: 120 }}>{key}:</Typography>
                      <Typography variant="body2"
                        sx={{ fontFamily: 'monospace' }}>{rest.join(': ')}</Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        );
      }

      return (
        <Card variant="outlined" sx={{ mb: 2, mx: 1, bgcolor: '#f8f9fa' }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            {nonEmpty.map((line, j) => (
              <Typography key={j}
                variant={j === 0 ? 'body1' : 'caption'}
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: j === 0 ? 600 : 400,
                  color: j > 0 ? 'text.secondary' : 'text.primary',
                }}>
                {line}
              </Typography>
            ))}
          </CardContent>
        </Card>
      );
    }

    case 'chipList':
      return (
        <Stack spacing={1} sx={{ ml: block.indented ? 1 : 0 }}>
          {block.items.map((item, j) => (
            <Stack key={j} direction="row" spacing={1} alignItems="center">
              <Chip label={item.label} size="small" variant="outlined" />
              <Typography variant="body2">{item.value}</Typography>
            </Stack>
          ))}
        </Stack>
      );

    case 'list':
      return (
        <Stack spacing={0.5} sx={{ ml: 3, mb: 2 }}>
          {block.items.map((item, j) => (
            <Typography key={j} variant="body2">
              • {renderInline(item)}
            </Typography>
          ))}
        </Stack>
      );

    case 'paragraph':
      return (
        <Typography paragraph sx={{ ml: block.indented ? 1 : 0 }}>
          {renderInline(block.text)}
        </Typography>
      );

    default:
      return null;
  }
}

/* ── Main component ──────────────────────────────────────────────── */
const HelpView = () => {
  const [expanded, setExpanded] = useState('panel-0');
  const [helpData, setHelpData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/content/help.md`)
      .then(res => {
        if (!res.ok) throw new Error(`Failed to load help content (${res.status})`);
        return res.text();
      })
      .then(md => setHelpData(parseHelpMarkdown(md)))
      .catch(err => setError(err.message));
  }, []);

  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Could not load help content: {error}</Alert>
      </Box>
    );
  }

  if (!helpData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const { frontMatter, sections } = helpData;

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto', p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Help sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Help &amp; Instructions
        </Typography>
      </Stack>

      {frontMatter.subtitle && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {frontMatter.subtitle}
        </Typography>
      )}

      <Divider sx={{ mb: 3 }} />

      {sections.map((section, idx) => {
        const panelId = `panel-${idx}`;
        const icon = SECTION_ICONS[section.title] || DEFAULT_ICON;
        return (
          <Accordion
            key={panelId}
            expanded={expanded === panelId}
            onChange={handleChange(panelId)}
            sx={{
              mb: 1,
              '&:before': { display: 'none' },
              borderRadius: '8px !important',
              border: '1px solid',
              borderColor: expanded === panelId ? 'primary.light' : 'divider',
              boxShadow: expanded === panelId ? 2 : 0,
              transition: 'all 0.2s ease',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
                bgcolor: expanded === panelId ? 'action.hover' : 'transparent',
              }}
            >
              {icon}
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1, pb: 2, px: 3 }}>
              {section.blocks.map((block, bIdx) => (
                <RenderBlock key={bIdx} block={block} idx={bIdx} />
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {frontMatter.contact && (
        <Card variant="outlined" sx={{ mt: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              For additional support, contact your system administrator at{' '}
              <strong>{frontMatter.contact}</strong>
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default HelpView;
